/* eslint-disable no-mixed-spaces-and-tabs */
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { Box, Typography } from "@mui/material";
import { DataGridPro, GridValidRowModel } from "@mui/x-data-grid-pro-6";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useDataGridLocaleText } from "../../../../../../design/use-datagrid-locale-text";
import useWorkersData from "../../../../controllers/use-workers-data";
import { getAllSkills, getSkillTableRows } from "../../../helper";
import useWorkerSkillColumn from "../../controllers/use-worker-skill-column";

const NoSkillsAvailable = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center">
        <BuildOutlinedIcon sx={{ color: "#00000061", height: 30, width: 30 }} />
        <Typography
          sx={{
            fontFamily: "Roboto",
            fontWeight: 400,
            fontSize: "14px",
            color: "#00000061",
            lineHeight: "20px",
            letterSpacing: "0.17px",
          }}
        >
          {t("workers_no-skills-found")}
        </Typography>
      </Box>
    </div>
  );
};

interface Job {
  jobId: string;
  jobName: string;
  color: string;
  isExpired: boolean;
  skills: any[];
}

interface WorkerProfile {
  jobs: Job[];
  otherSkills: any[];
  pendingSkillEvaluationSkillIds?: number[];
  interestedSkills?: any[];
}

interface WorkerSkillsTableProps {
  workerProfile: WorkerProfile;
  searchText: string;
  skillExpiryEnabled: boolean;
  skillPriorityEnabled: boolean;
  allSkillsWithPriority?: any[];
  allSkills?: any[];
  isRestricted?: boolean;
}

const WorkerSkillsTable: React.FC<WorkerSkillsTableProps> = ({
  workerProfile,
  searchText,
  skillExpiryEnabled,
  skillPriorityEnabled,
  allSkillsWithPriority = [],
  allSkills = [],
  isRestricted = false,
}) => {
  const columns: any = useWorkerSkillColumn(searchText, isRestricted);
  const { deleteWorkerSkillStatus, fetchSkillsStatus } = useWorkersData();
  const dataGridLocaleText = useDataGridLocaleText();

  const jobs = workerProfile?.jobs || [];
  const otherSkills = workerProfile?.otherSkills || [];
  const pendingSkillEvaluationSkillIds = workerProfile?.pendingSkillEvaluationSkillIds || [];
  const interestedSkills = workerProfile?.interestedSkills || [];

  const rows = getAllSkills(
    jobs,
    otherSkills,
    skillPriorityEnabled,
    allSkillsWithPriority,
    allSkills,
    pendingSkillEvaluationSkillIds,
    interestedSkills
  );
  const isPagination = rows.length > 0;

  const filteredRows = getSkillTableRows(rows, searchText);

  return (
    <Box style={{ height: rows.length > 0 ? "auto" : 300 }}>
      <DataGridPro
        rows={(filteredRows as GridValidRowModel[]) ?? []}
        localeText={dataGridLocaleText}
        columnVisibilityModel={{
          // Hide certifications based on company settings
          status: skillExpiryEnabled,
          validFrom: skillExpiryEnabled,
          validUntil: skillExpiryEnabled,
          skillPriority: skillPriorityEnabled,
        }}
        columns={columns}
        pagination={isPagination}
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        disableColumnPinning
        loading={deleteWorkerSkillStatus === "pending" || fetchSkillsStatus === "pending"}
        slots={{
          noRowsOverlay: NoSkillsAvailable,
        }}
        initialState={{
          ...rows,
          pagination: { paginationModel: { pageSize: 10 } },
          filter: {},
        }}
        pageSizeOptions={[10, 25, 50]}
        autoHeight
        getRowHeight={() => "auto"}
        sx={{
          position: "sticky",
          border: "none !important",
          ".MuiDataGrid-cell": {
            "&:focus": {
              outline: "none",
            },
            "&.hover-column-cell": {
              position: "sticky",
              right: 24,
              zIndex: 1,
            },
            "& .hover-content": {
              transition: "opacity 0.3s ease-in-out",
              opacity: 0,
            },
          },
          ".MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-columnHeader:focus-within": {
            outline: "none",
          },
          ".MuiDataGrid-row:hover .hover-column-cell .hover-content": {
            opacity: 1,
            backgroundColor: "#f5f5f5",
          },
        }}
      />
    </Box>
  );
};

export default WorkerSkillsTable;
